import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { LogoWithText } from "./Logo";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: "/#tjanster", label: "Tjänster" },
    { href: "/#varfor-oss", label: "Varför oss?" },
    { href: "/#kontakt", label: "Kontakt" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-slate-200/70 shadow-sm"
          : "bg-white/60 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-home-link" className="flex items-center">
          <LogoWithText />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-700 hover:text-green-700 transition-colors"
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/boka"
            data-testid="header-boka-btn"
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Boka nu
          </Link>
          <button
            className="md:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Meny"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-base font-medium text-slate-800 py-2"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/boka"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-green-600 text-white font-semibold px-5 py-3 text-base"
              data-testid="mobile-boka-btn"
            >
              Boka nu
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer
      id="kontakt"
      className="bg-slate-900 text-slate-300"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <div className="text-white font-extrabold text-xl tracking-tight">
              KJ Grönt &amp; Rent
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-green-400 font-semibold mt-1">
              Vänge
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">
              Pålitliga och noggranna trädgårds- och biltvättstjänster för
              hushåll i Vänge med omnejd.
            </p>
          </div>

          <div>
            <div className="text-white font-semibold mb-4">Kontakt</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-green-400" />
                <a
                  href="tel:0761479408"
                  className="hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  076-1479408
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-green-400" />
                <a
                  href="mailto:kjgrontochrent@gmail.com"
                  className="hover:text-white transition-colors break-all"
                  data-testid="footer-email"
                >
                  kjgrontochrent@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-green-400" />
                <span data-testid="footer-address">Vänge, Sverige</span>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-white font-semibold mb-4">Tjänster</div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>Gräsklippning</li>
              <li>Häcktrimning</li>
              <li>Biltvätt</li>
            </ul>
            <Link
              to="/boka"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 text-sm shadow-sm transition-colors"
              data-testid="footer-boka-btn"
            >
              Få prisförslag
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-xs text-slate-500">
          <div>© {new Date().getFullYear()} KJ Grönt och Rent — Vänge</div>
          <div>Byggt med omsorg i Sverige</div>
        </div>
      </div>
    </footer>
  );
};
